import { useCallback, useEffect, useRef, useState } from 'react';

import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';

const MOBILE_OFFSET_HEIGHT = 160;
const DESKTOP_OFFSET_HEIGHT = 205;

export const useLeagueScrollDetection = (leagues: { slug: string }[]) => {
  const leagueRefs = useRef<Map<string, HTMLElement>>(new Map());

  const registerLeagueRef = useCallback(
    (slug: string, element: HTMLElement | null) => {
      if (element) {
        leagueRefs.current.set(slug, element);
      } else {
        leagueRefs.current.delete(slug);
      }
    },
    []
  );

  const isTabletAndAbove = useIsTabletAndAbove();
  const headerHeight = isTabletAndAbove
    ? DESKTOP_OFFSET_HEIGHT
    : MOBILE_OFFSET_HEIGHT;

  const [visibleLeague, setVisibleLeague] = useState<string>(
    leagues[0]?.slug || ''
  );

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined' || leagues.length === 0) {
      return () => {};
    }

    const intersectionRatios = new Map<string, number>();

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const leagueSlug = entry.target.getAttribute('data-league-slug');
          if (leagueSlug) {
            intersectionRatios.set(leagueSlug, entry.intersectionRatio);
          }
        });

        let bestLeague = '';
        let bestRatio = 0;

        intersectionRatios.forEach((ratio, slug) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestLeague = slug;
          }
        });

        if (bestLeague && bestRatio > 0.1) {
          setVisibleLeague(prev => (bestLeague === prev ? prev : bestLeague));
        }
      },
      {
        rootMargin: `-${headerHeight}px 0px -30% 0px`,
        threshold: [0, 0.1, 0.5, 1],
      }
    );

    // Observe all registered league elements
    leagueRefs.current.forEach((element, slug) => {
      element.setAttribute('data-league-slug', slug);
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };
  }, [headerHeight, leagues]);

  return { visibleLeague, leagueRefs, headerHeight, registerLeagueRef };
};
